import React from 'react';
import { useTranslation } from 'react-i18next';
import BasicButton from '@/components/styled/BasicButton';
import { track } from '@/analytics';
import sanitize from 'sanitize-html';
import { FileText } from 'react-feather';
import { useCreatePageWithContent } from '@/hooks/useCreatePageWithContent';
import { useRewritePageContent } from '@/hooks/useRewritePageContent';

export type CreatePageData = {
    title: string;
};

export type CreatePageWithContentData = {
    title: string;
    content: string;
};

export type RewritePageContentData = {
    pageId: string;
    content: string;
};

export type AIToolConfirmationCardProps =
    | {
          type: 'create_page';
          data: CreatePageData;
          onConfirm: () => void;
          onCancel: () => void;
      }
    | {
          type: 'create_page_with_content';
          data: CreatePageWithContentData;
          onConfirm: () => void;
          onCancel: () => void;
      }
    | {
          type: 'rewrite_page_content';
          data: RewritePageContentData;
          onConfirm: () => void;
          onCancel: () => void;
      };

const AIToolConfirmationCard = (props: AIToolConfirmationCardProps) => {
    if (props.type === 'create_page') {
        return <CreatePageConfirmation data={props.data} onConfirm={props.onConfirm} onCancel={props.onCancel} />;
    }

    if (props.type === 'create_page_with_content') {
        return (
            <CreatePageWithContentConfirmation
                data={props.data}
                onConfirm={props.onConfirm}
                onCancel={props.onCancel}
            />
        );
    }

    if (props.type === 'rewrite_page_content') {
        return (
            <RewritePageContentConfirmation data={props.data} onConfirm={props.onConfirm} onCancel={props.onCancel} />
        );
    }

    return null;
};

type BaseCreatePageProps = {
    title: string;
    content?: string;
    onConfirm: () => void;
    onCancel: () => void;
    trackingPrefix: string;
    infoMessage?: string;
};

// Shared component for both create page variants
const CreatePageConfirmationBase = ({
    title,
    content,
    onConfirm,
    onCancel,
    trackingPrefix,
    infoMessage,
}: BaseCreatePageProps) => {
    const { t } = useTranslation();
    const { createPageWithContent } = useCreatePageWithContent();
    const [pageTitle, setPageTitle] = React.useState(title || 'Untitled Page');
    const [isCreating, setIsCreating] = React.useState(false);

    // Sanitize title: remove all HTML tags and trim whitespace
    const sanitizedTitle = React.useMemo(() => {
        return sanitize(pageTitle, { allowedTags: [], allowedAttributes: {} }).trim();
    }, [pageTitle]);

    const handleConfirm = React.useCallback(async () => {
        if (isCreating) return; // Prevent double clicks

        setIsCreating(true);
        track(`${trackingPrefix}-confirmed`);

        try {
            const result = await createPageWithContent(sanitizedTitle, content);

            if (!result.success) {
                console.error('Failed to create page:', result.error);
                // TODO: Show error toast to user
                setIsCreating(false);
                return;
            }

            onConfirm();
        } catch (error) {
            console.error('Failed to create page:', error);
            setIsCreating(false);
        }
    }, [createPageWithContent, sanitizedTitle, content, onConfirm, trackingPrefix, isCreating]);

    const handleCancel = React.useCallback(() => {
        track(`${trackingPrefix}-cancelled`);
        onCancel();
    }, [onCancel, trackingPrefix]);

    return (
        <div className="bg-white dark:bg-saga-gray-900 rounded-lg pt-2 pb-3 px-3 shadow-popupBorder">
            <div className="flex items-center space-x-2 mb-3">
                <FileText className="w-4 h-4 text-saga-text dark:text-saga-gray-300" />
                <input
                    type="text"
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                    className="w-full flex-1 pr-3 py-1.5 text-base rounded-md dark:bg-saga-gray-900 dark:text-saga-gray-300 placeholder-saga-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={t('ai.placeholder', 'Enter page title')}
                    maxLength={300}
                    autoFocus
                    disabled={isCreating}
                />
            </div>
            {infoMessage && <div className="text-sm text-saga-gray-500 mb-3 px-1">{infoMessage}</div>}
            <div className="flex justify-end space-x-2">
                <BasicButton
                    className="h-8 px-4"
                    variant="secondary_inverted"
                    size="small"
                    onClick={handleCancel}
                    disabled={isCreating}
                >
                    {t('common.cancel', 'Cancel')}
                </BasicButton>
                <BasicButton
                    className="h-8 px-4"
                    size="small"
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={isCreating}
                >
                    {isCreating ? t('ai.creating', 'Creating...') : t('ai.confirm', 'Create')}
                </BasicButton>
            </div>
        </div>
    );
};

type CreatePageConfirmationProps = {
    data: CreatePageData;
    onConfirm: () => void;
    onCancel: () => void;
};

const CreatePageConfirmation = ({ data, onConfirm, onCancel }: CreatePageConfirmationProps) => {
    return (
        <CreatePageConfirmationBase
            title={data.title}
            content={undefined} // No content for basic page creation
            onConfirm={onConfirm}
            onCancel={onCancel}
            trackingPrefix="ai-create-page"
        />
    );
};

type CreatePageWithContentConfirmationProps = {
    data: CreatePageWithContentData;
    onConfirm: () => void;
    onCancel: () => void;
};

const CreatePageWithContentConfirmation = ({ data, onConfirm, onCancel }: CreatePageWithContentConfirmationProps) => {
    const { t } = useTranslation();

    return (
        <CreatePageConfirmationBase
            title={data.title}
            content={data.content}
            onConfirm={onConfirm}
            onCancel={onCancel}
            trackingPrefix="ai-create-page-with-content"
            infoMessage={t('ai.createPageWithContent.info', 'Page will be created with AI-generated content.')}
        />
    );
};

type RewritePageContentConfirmationProps = {
    data: RewritePageContentData;
    onConfirm: () => void;
    onCancel: () => void;
};

const RewritePageContentConfirmation = ({ data, onConfirm, onCancel }: RewritePageContentConfirmationProps) => {
    const { t } = useTranslation();
    const { rewritePageContent } = useRewritePageContent();
    const [isRewriting, setIsRewriting] = React.useState(false);

    const handleConfirm = React.useCallback(async () => {
        if (isRewriting) return; // Prevent double clicks

        setIsRewriting(true);
        track('ai-rewrite-page-content-confirmed');

        try {
            const result = await rewritePageContent(data.pageId, data.content);

            if (!result.success) {
                console.error('Failed to rewrite page:', result.error);
                // TODO: Show error toast to user
                setIsRewriting(false);
                return;
            }

            onConfirm();
        } catch (error) {
            console.error('Failed to rewrite page:', error);
            setIsRewriting(false);
        }
    }, [rewritePageContent, data.pageId, data.content, onConfirm, isRewriting]);

    const handleCancel = React.useCallback(() => {
        track('ai-rewrite-page-content-cancelled');
        onCancel();
    }, [onCancel]);

    return (
        <div className="bg-white dark:bg-saga-gray-900 rounded-lg pt-2 pb-3 px-3 shadow-popupBorder">
            <div className="flex items-center space-x-2 mb-3">
                <FileText className="w-4 h-4 text-saga-text dark:text-saga-gray-300" />
                <span className="text-base text-saga-text dark:text-saga-gray-300 font-medium">
                    {t('ai.rewritePageContent.title', 'Rewrite Page Content')}
                </span>
            </div>
            <div className="text-sm text-saga-gray-500 mb-3 px-1">
                {t('ai.rewritePageContent.info', 'This will replace all content on the current page except the title.')}
            </div>
            <div className="flex justify-end space-x-2">
                <BasicButton
                    className="h-8 px-4"
                    variant="secondary_inverted"
                    size="small"
                    onClick={handleCancel}
                    disabled={isRewriting}
                >
                    {t('common.cancel', 'Cancel')}
                </BasicButton>
                <BasicButton
                    className="h-8 px-4"
                    size="small"
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={isRewriting}
                >
                    {isRewriting ? t('ai.rewriting', 'Rewriting...') : t('ai.rewrite', 'Rewrite')}
                </BasicButton>
            </div>
        </div>
    );
};

export default AIToolConfirmationCard;
